import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { adminBrightPatternTemplateApi } from '../../api/adminBrightPatternTemplate.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import '../subscription-plans/subscription-plans.css';
import './bright-pattern-templates.css';

const emptyForm = {
  name: '',
  slug: '',
  isActive: true,
  displayOrder: 0,
};

function slugifyName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

export default function BrightPatternTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isNew = templateId === 'new';
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew || !templateId) return;
    const load = async () => {
      const result = await apiHandler(() => adminBrightPatternTemplateApi.getById(templateId));
      if (result.success) {
        setForm({ ...emptyForm, ...unwrapData(result) });
      } else {
        setError(result.message || 'Failed to load template');
      }
      setLoading(false);
    };
    load();
  }, [templateId, isNew]);

  const handleNameChange = (name) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: isNew ? slugifyName(name) : prev.slug,
    }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const payload = isNew
      ? {
          name: form.name.trim(),
          slug: form.slug.trim(),
          isActive: Boolean(form.isActive),
          displayOrder: Number(form.displayOrder || 0),
        }
      : {
          name: form.name.trim(),
          isActive: Boolean(form.isActive),
          displayOrder: Number(form.displayOrder || 0),
        };
    const result = await apiHandler(() => (
      isNew
        ? adminBrightPatternTemplateApi.create(payload)
        : adminBrightPatternTemplateApi.update(templateId, payload)
    ));
    if (result.success) {
      navigate('/bright-pattern-templates');
    } else {
      setError(result.message || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="sp-page sp-loading">
        <i className="fa-solid fa-spinner fa-spin" /> Loading template…
      </div>
    );
  }

  return (
    <div className="sp-page bpt-page">
      <PageHeader
        title={isNew ? 'Create Bright Pattern Template' : `Edit ${form.name || 'Template'}`}
        subtitle="Dashboard type identifier for Bright Pattern contact center"
        actions={(
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/bright-pattern-templates')}>
            <i className="fa-solid fa-arrow-left" /> All templates
          </button>
        )}
      />

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}

      <div className="bpt-form-card">
        <div className="bpt-form-row">
          <label>Name</label>
          <input
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Urgent Assist"
          />
        </div>
        <div className="bpt-form-row">
          <label>Slug</label>
          <input
            value={form.slug}
            readOnly
            placeholder="urgent_assist"
            className="bpt-readonly"
          />
          <p className="bpt-hint">
            {isNew
              ? 'Auto-generated from name when you create the template.'
              : 'Slug cannot be changed after creation.'}
          </p>
        </div>
        {!isNew && (
          <div className="bpt-form-row">
            <label className="sp-toggle">
              <input
                type="checkbox"
                checked={!!form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
        )}
        <div className="bpt-footer-actions">
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/bright-pattern-templates')}>
            Cancel
          </button>
          <button type="button" className="sp-btn sp-btn-primary" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </div>
    </div>
  );
}
